import Modal from "@/components/controls/Modal";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { Close } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import CreateCompanyModal from "@/pages/companies/components/CreateCompanyModal";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import { DealData, DealStatus } from "@/pages/deals/types";
import { useCreateDealMutation } from "@/pages/deals/hooks";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import useBoundStore from "@/services/store/BoundStore";
import { mapEmptyToNull } from "@/utils/formatting";
import { useProducts } from "@/entities/settings/hooks";
import { useEntityFields } from "@/pages/settings/hooks";
import { useCompanies } from "@/pages/companies/hooks";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { excludePartnerFields } from "@/entities/dataTables/helpers";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  buildMapEmptyToNullObject
} from "@/utils/entityFieldsUtils/formFields";
import { LeadData } from "@/pages/leads/types";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { getUsersOptions } from "@/entities/forms/helpers";
import UserAvatar from "@/components/controls/UserAvatar";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  lead: LeadData;
  handleConvertLead: () => void;
}

const TRANSLATION_KEY = "leads.convertToDealModal";

const ConvertLeadModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  lead,
  handleConvertLead
}) => {
  const { t } = useTranslation();

  const formRef = useRef<HTMLFormElement>(null);
  const createDeal = useCreateDealMutation();

  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();

  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [view, setView] = useState<"deals" | "companies" | "people">("deals");
  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(
    lead?.companyId || ""
  );
  const [selectedPersonId, setSelectedPersonId] = useState<string>(
    lead?.contactId || ""
  );
  const [hasUserEnteredDealName, setHasUserEnteredDealName] = useState(false);
  const [currentProductId, setCurrentProductId] = useState<string>("");

  const products = useProducts().data?.products ?? [];
  const stages = useDealStages().data?.stages;

  const { data: entityFieldsResponse } = useEntityFields(EntityType.DEAL);
  const enablePartnerships = useEnablePartnerships();
  const visibleFields = useMemo(
    () =>
      enablePartnerships
        ? entityFieldsResponse?.fields
        : excludePartnerFields(entityFieldsResponse?.fields ?? []),
    [entityFieldsResponse?.fields, enablePartnerships]
  );

  const users = useBoundStore((state) => state.users.entities);
  const getCompany = useBoundStore((state) => state.companies.getEntity);

  // Use companies hook to fetch company data when needed
  const companiesQuery = useCompanies(
    selectedCompanyId ? [selectedCompanyId] : []
  );
  const companyData = companiesQuery.entities?.[0];

  const getCompanyName = (companyId: string): string | null => {
    if (companyId === lead?.companyId && lead?.companyName) {
      return lead.companyName;
    }

    // Try store first, then fetched data
    const company = getCompany(companyId);
    if (company?.name) return company.name;

    if (companyData?.id === companyId) return companyData.name || null;

    return null;
  };

  const autoPopulateDealName = (
    companyName: string,
    currentProductId?: string
  ) => {
    if (!hasUserEnteredDealName && typeof formSetValue === "function") {
      let dealName = companyName;

      // Only add product name if a product is explicitly selected
      if (currentProductId) {
        const selectedProduct = products.find(
          (product) => product.id === currentProductId
        );
        if (selectedProduct) {
          dealName = `${companyName} ${selectedProduct.name}`;
        }
      }

      formSetValue("name", dealName, {
        shouldValidate: true
      });
    }
  };

  const onCompanySelect = (companyId: string) => {
    setSelectedCompanyId(companyId);
    setHasUserEnteredDealName(false);
    const companyName = getCompanyName(companyId);
    if (companyName) {
      autoPopulateDealName(companyName, currentProductId || undefined);
    }
  };

  const onProductSelect = (productId: string) => {
    setCurrentProductId(productId);
    setHasUserEnteredDealName(false);
    if (selectedCompanyId) {
      const companyName = getCompanyName(selectedCompanyId);
      if (companyName) {
        autoPopulateDealName(companyName, productId);
      }
    }
  };

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData: (lead?.aeOwner && users[lead.aeOwner]) || Object.values(users)[0], // Use lead owner or first user
    selectedCompanyId,
    selectedPersonId,
    setSelectedCompanyId,
    setSelectedPersonId,
    setHasUserEnteredDealName,
    autoPopulateDealName,
    setView: (view) => {
      if (view === "companies" || view === "people" || view === "deals") {
        setView(view);
      }
    },
    hasUserEnteredDealName,
    onCompanySelect,
    onProductSelect,
    entityType: EntityType.DEAL
  };

  const baseSections = createFormNewSectionsFromAPI(
    visibleFields,
    t,
    formFieldsContext
  );

  const formSections: FormSectionProps[] = useMemo(
    () =>
      baseSections.map((section) => ({
        ...section,
        fields: section.fields?.map((field) => {
          const enhancedField: FormFieldNewType = {
            ...field,
            labelPosition: "left" as const,
            // Pre-populate fields from lead data
            defaultValue: lead
              ? getLeadDefaultValue(field.name, lead)
              : undefined,
            // Add key to force re-render when company or product changes
            ...(field.name === "name" && {
              key: `name-${selectedCompanyId}-${currentProductId}`
            })
          };

          if (field.name === "managedBy") {
            enhancedField.fieldActionProps = {
              fieldName: field.name,
              options: getUsersOptions(users),
              showSearch: true,
              renderIcon: (value: string | number) => (
                <UserAvatar userData={users[String(value)]} size="M" />
              )
            };
          }

          return enhancedField;
        })
      })),
    [baseSections, lead, products, currentProductId, selectedCompanyId, users]
  );

  function generateDefaultDealName(leadData: LeadData): string {
    const companyIdToUse = selectedCompanyId || leadData.companyId || "";
    const productIdToUse = currentProductId || leadData.productId || "";

    const companyName =
      getCompanyName(companyIdToUse) || leadData.companyName || "";
    if (!companyName) return "";

    let dealName = companyName;

    if (productIdToUse) {
      const selectedProduct = products.find(
        (product) => product.id === productIdToUse
      );
      if (selectedProduct) {
        dealName = `${companyName} ${selectedProduct.name}`;
      }
    }

    return dealName;
  }

  function getLeadDefaultValue(fieldName: string, leadData: LeadData) {
    switch (fieldName) {
      case "companyId":
        return leadData.companyId || "";
      case "contactId":
        return leadData.contactId || "";
      case "productId":
        return leadData.productId || "";
      case "licenseId":
        return leadData.licenseId || "";
      case "numberOfLicenses":
        return leadData.numberOfLicenses || null;
      case "licensesInDeal":
        return leadData.numberOfLicenses || null;
      case "aeConfidenceLevel":
        return leadData.aeConfidenceLevel || null;
      case "managedBy":
        return leadData.aeOwner || "";
      case "name":
        return generateDefaultDealName(leadData);
      default:
        return undefined;
    }
  }

  useEffect(() => {
    if (!isOpen) {
      formRef.current?.reset();
      setSelectedCompanyId(lead?.companyId || "");
      setSelectedPersonId(lead?.contactId || "");
      setCurrentProductId("");
      setView("deals");
      setHasUserEnteredDealName(false);
    } else {
      if (lead?.productId) {
        setCurrentProductId(lead.productId);
      }
    }
  }, [isOpen, lead]);

  useEffect(() => {
    if (lead && typeof formSetValue === "function") {
      if (lead.companyId) {
        formSetValue("companyId", lead.companyId, { shouldValidate: true });
        setSelectedCompanyId(lead.companyId);
      }

      if (lead.contactId) {
        formSetValue("contactId", lead.contactId, { shouldValidate: true });
        setSelectedPersonId(lead.contactId);
      }

      if (lead.productId) {
        formSetValue("productId", lead.productId, { shouldValidate: true });
        setCurrentProductId(lead.productId);
      }

      if (lead.licenseId) {
        formSetValue("licenseId", lead.licenseId, { shouldValidate: true });
      }

      if (lead.numberOfLicenses) {
        formSetValue("numberOfLicenses", lead.numberOfLicenses, {
          shouldValidate: true
        });
        formSetValue("licensesInDeal", lead.numberOfLicenses, {
          shouldValidate: true
        });
      }

      if (lead.aeConfidenceLevel) {
        formSetValue("aeConfidenceLevel", lead.aeConfidenceLevel, {
          shouldValidate: true
        });
      }

      if (lead.aeOwner) {
        formSetValue("managedBy", lead.aeOwner, { shouldValidate: true });
      }
    }
  }, [lead, formSetValue]);

  useEffect(() => {
    if (
      isOpen &&
      lead &&
      !hasUserEnteredDealName &&
      typeof formSetValue === "function" &&
      selectedCompanyId &&
      selectedCompanyId === lead.companyId // Only run for initial lead data
    ) {
      const companyName = getCompanyName(selectedCompanyId);
      if (companyName) {
        const productIdToUse = lead.productId;
        autoPopulateDealName(companyName, productIdToUse || undefined);
      }
    }
  }, [
    isOpen,
    lead,
    selectedCompanyId,
    hasUserEnteredDealName,
    formSetValue,
    companyData,
    lead?.companyName
  ]);

  const handleSubmit = async (data: DealData) => {
    if (!stages) return;

    const transformFormData: DealData = {
      ...data,
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data)),
      // Handle ARR/MRR field dynamically from the form data
      ...(data.arr !== undefined && {
        arr: data.arr ? Number(data.arr) : null
      }),
      ...(data.mrr !== undefined && {
        mrr: data.mrr ? Number(data.mrr) : null
      }),
      status: DealStatus.IN_PROGRESS,
      stage: stages.sort((a, b) => a.stageOrder - b.stageOrder)[0].id,
      leadId: lead.id, // Include lead ID for conversion
      manuallyAddedPeople: data.contactId ? [data.contactId] : []
    };

    await createDeal.mutateAsync(transformFormData, {
      onSuccess: async (response) => {
        toast.success(
          <ToastBody
            title={t(`${TRANSLATION_KEY}.successToast.title`)}
            subtitle={t(`${TRANSLATION_KEY}.successToast.subtitle`)}
            actions={
              <>
                <Button
                  onClick={() => {
                    toast.dismiss();
                    newOpenDrawer(response.id, EntityType.DEAL);
                  }}
                  variant="secondaryNoBg"
                >
                  {t("deals.createModal.view")}
                </Button>
                <IconButton
                  size="md"
                  variant="iconNoBg"
                  iconColor="secondary"
                  onClick={() => toast.dismiss()}
                >
                  <Close />
                </IconButton>
              </>
            }
          />,
          {
            duration: 5000
          }
        );
        handleConvertLead(); // Call the lead conversion callback
        setIsOpen(false);
      },
      onError: (_error: Error) => {
        toast.error(
          <ToastBody
            title={t("deals.createModal.error")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
      }
    });
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
  };

  const onCompanyCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedCompanyId(id);
    setHasUserEnteredDealName(false);
    if (typeof formSetValue === "function") {
      formSetValue("companyId", id, {
        shouldValidate: true
      });

      autoPopulateDealName(name, currentProductId || undefined);
    }
  };
  const onPersonCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedPersonId(id);
    if (typeof formSetValue === "function") {
      formSetValue("contactId", id, {
        shouldValidate: true
      });
    }
  };

  if (!lead) return null;

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        title={
          <div>
            <Text as="div" variant="lgSubtitleH1">
              {t(`${TRANSLATION_KEY}.title`)}
            </Text>
            <Text as="div" variant="lgBodySecondary">
              {t(`${TRANSLATION_KEY}.subTitle`)}
            </Text>
          </div>
        }
        content={
          <div className="flex flex-col gap-3 w-[35rem] p-4 h-[60vh] overflow-y-auto">
            <FormNew
              sections={formSections}
              isFormValid={(isValid: boolean) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
            />
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              data-test="cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Button
              data-test="submit-button"
              disabled={!isFormValid || createDeal.isPending || !stages}
              onClick={handleCreateNew}
            >
              {t("deals.createModal.createNew")}
            </Button>
          </div>
        }
      />
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.DEAL}
        onClose={newCloseDrawer}
      />
      <CreateCompanyModal
        isOpen={view === "companies"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("deals");
          }
        }}
        onSuccess={onCompanyCreation}
      />
      <CreatePersonModal
        isOpen={view === "people"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("deals");
          }
        }}
        companyId={selectedCompanyId}
        onSuccess={onPersonCreation}
      />
    </>
  );
};

export default ConvertLeadModal;
